import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { Call } from "@sales-ops-amplify/shared";
import { askAboutCall, regenerateInsights } from "@/api/calls";

type Props = {
  call: Call;
  onCallUpdated?: (call: Call) => void;
  /** Auto-generate when the call has no insights yet (default true). */
  autoGenerate?: boolean;
};

export function CallInsightsPanel({
  call,
  onCallUpdated,
  autoGenerate = true,
}: Props) {
  const [question, setQuestion] = useState("");
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const autoTriedFor = useRef<string | null>(null);

  const refreshMut = useMutation({
    mutationFn: () => regenerateInsights(call.callId),
    onSuccess: (res) => onCallUpdated?.(res.call),
  });

  const askMut = useMutation({
    mutationFn: (q: string) => askAboutCall(call.callId, q),
    onSuccess: (res) => {
      onCallUpdated?.(res.call);
      setQuestion("");
    },
  });

  const insights = call.insights;
  const hasInsights = Boolean(
    insights?.generatedAt ||
      call.aiSummary ||
      (insights?.nextSteps && insights.nextSteps.length > 0),
  );
  const draft = insights?.followUpEmailDraft;
  const generating = refreshMut.isPending;

  // Auto-generate once per call when selecting a call without insights.
  useEffect(() => {
    if (!autoGenerate) return;
    if (hasInsights) return;
    if (autoTriedFor.current === call.callId) return;
    if (refreshMut.isPending) return;
    autoTriedFor.current = call.callId;
    refreshMut.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: fire once per callId without insights
  }, [autoGenerate, call.callId, hasInsights]);

  async function copyDraft() {
    if (!draft) return;
    const text = `Subject: ${draft.subject}\n\n${draft.bodyText}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback("Draft copied");
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch {
      setCopyFeedback("Copy failed");
    }
  }

  return (
    <div className="space-y-3 rounded-md border border-brand/25 bg-brand/[0.03] p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold tracking-tight">
            AI Call Insights
          </p>
          <p className="text-xs text-muted-foreground">
            Outcome, objections, next steps, and follow-up suggestions
          </p>
          {insights?.score != null && hasInsights && (
            <p className="mt-1 text-xs text-muted-foreground">
              Score {Math.round(insights.score)}/100
              {insights.sentiment ? ` · ${insights.sentiment}` : ""}
            </p>
          )}
        </div>
        <button
          type="button"
          className="shrink-0 rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium"
          disabled={generating}
          onClick={() => refreshMut.mutate()}
        >
          {generating
            ? "Generating…"
            : hasInsights
              ? "Regenerate"
              : "Generate"}
        </button>
      </div>

      {generating && !hasInsights && (
        <div className="space-y-2 py-2">
          <div className="h-3 w-3/4 animate-pulse rounded bg-surface-hover" />
          <div className="h-3 w-1/2 animate-pulse rounded bg-surface-hover" />
          <div className="h-3 w-2/3 animate-pulse rounded bg-surface-hover" />
          <p className="text-xs text-muted-foreground">
            Analyzing call notes, duration, and recording…
          </p>
        </div>
      )}

      {!generating && !hasInsights && (
        <div className="rounded-md border border-dashed border-border bg-background/70 px-3 py-4 text-center">
          <p className="text-sm font-medium">No insights yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Generate AI insights from notes, disposition, and recording
            metadata for this call.
          </p>
          <button
            type="button"
            className="mt-3 rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-white"
            onClick={() => refreshMut.mutate()}
          >
            Generate AI insights
          </button>
          {refreshMut.isError && (
            <p className="mt-2 text-xs text-red-600">
              {refreshMut.error instanceof Error
                ? refreshMut.error.message
                : "Insight generation failed"}
            </p>
          )}
        </div>
      )}

      {hasInsights && (
        <>
          {(call.aiSummary || insights?.outcome) && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Outcome
              </p>
              <p className="mt-1 text-sm leading-relaxed">
                {call.aiSummary || insights?.outcome}
              </p>
            </div>
          )}

          {insights?.nextSteps && insights.nextSteps.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Next steps
              </p>
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-sm">
                {insights.nextSteps.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </div>
          )}

          {insights?.objections && insights.objections.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Objections
              </p>
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-sm">
                {insights.objections.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </div>
          )}

          {insights?.talkingPoints && insights.talkingPoints.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Talking points
              </p>
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-sm">
                {insights.talkingPoints.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </div>
          )}

          {draft && (
            <div className="rounded-md border border-border/80 bg-background p-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Follow-up email draft
                </p>
                <button
                  type="button"
                  className="text-xs text-brand underline"
                  onClick={() => void copyDraft()}
                >
                  Copy draft
                </button>
              </div>
              <p className="mt-1 text-xs font-medium">{draft.subject}</p>
              <p className="mt-1 max-h-28 overflow-y-auto whitespace-pre-wrap text-xs text-muted-foreground">
                {draft.bodyText}
              </p>
              {copyFeedback && (
                <p className="mt-1 text-xs text-brand">{copyFeedback}</p>
              )}
            </div>
          )}

          {call.transcript && (
            <details className="text-xs">
              <summary className="cursor-pointer font-medium text-muted-foreground">
                Transcript
              </summary>
              <p className="mt-1 max-h-40 overflow-y-auto whitespace-pre-wrap">
                {call.transcript}
              </p>
            </details>
          )}
        </>
      )}

      <div className="space-y-2 border-t border-border/80 pt-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Ask AI about this call
        </p>
        <div className="flex gap-2">
          <input
            className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            placeholder="e.g. What objections came up?"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && question.trim()) {
                askMut.mutate(question.trim());
              }
            }}
          />
          <button
            type="button"
            className="rounded-md bg-brand px-2.5 py-1.5 text-xs text-white disabled:opacity-40"
            disabled={!question.trim() || askMut.isPending}
            onClick={() => askMut.mutate(question.trim())}
          >
            {askMut.isPending ? "…" : "Ask"}
          </button>
        </div>
        {(insights?.askAnswers ?? []).slice(-3).reverse().map((a) => (
          <div
            key={`${a.at}-${a.question}`}
            className="rounded-md bg-background/80 p-2 text-xs"
          >
            <p className="font-medium">Q: {a.question}</p>
            <p className="mt-1 text-muted-foreground">A: {a.answer}</p>
          </div>
        ))}
        {askMut.isError && (
          <p className="text-xs text-red-600">
            {askMut.error instanceof Error
              ? askMut.error.message
              : "Could not get an answer."}
          </p>
        )}
      </div>
    </div>
  );
}
